// Angular imports
import { CurrencyPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, NgZone, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Params, QueryParamsHandling, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, catchError, forkJoin, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

// Models
import { ApiResponse } from '../models/api-response.model';
import { ItemVariation } from '../models/item-variation.model';
import { Item } from '../models/item.model';
import { MembershipDefinition } from '../models/membership-definition.model';
import { SaleItem } from '../models/sale-item.model';
import { SalesSummary } from '../models/sales-summary.model';

// Custom services
import { datadogRum } from '@datadog/browser-rum';
import { SeAnalyticsService } from 'se-analytics';
import { SeFeConfirmService } from 'se-fe-confirm';
import { SeFePaginationChange, SeFePaginationOptions } from 'se-fe-pagination';
import { SeFeToastService, SeFeToastTypes } from 'se-fe-toast';
import { AppExternalLinkService } from 'src/app/services/external-link.service';
import { LoadScriptsService } from 'src/app/services/load-scripts.service';
import { CURRENT_ORG_ID } from '../../providers/current-org-id.provider';
import { PriceSchedule } from '../models/price-schedule.model';
import { PagedParameters } from '../pagination/pagedParameters';
import { PagingService } from '../pagination/paging.service';
import { ItemVariationService } from '../services/item-variation.service';
import { ItemService } from '../services/item.service';
import { MembershipDefinitionService } from '../services/membership-definition.service';
import { PriceScheduleService } from '../services/price-schedule.service';
import { SaleItemService } from '../services/sale-item.service';
import { StoreStatusService } from '../services/store-status.service';
import { SurveyResultsService } from '../services/survey-results.service';

interface ItemDetailLoadError {
  source: 'item' | 'variations';
  error: HttpErrorResponse;
}

@Component({
  selector: 'app-item-detail',
  standalone: false,
  styleUrls: ['./item-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './item-detail.component.html',
})
export class ItemDetailComponent implements OnInit, OnDestroy {
  defaultVariation: ItemVariation;
  hasSaleItems: boolean;
  variationHasSku: boolean;
  variationStats: any = {};
  item: Item;
  itemId = this.activatedRoute.snapshot.params.saleItemId;
  saleItems: SaleItem[] = [];
  loadingSaleItems = true;
  showOverlay: boolean;
  loadingItem = true;
  salesSummary = '';
  showTaxCode: boolean;
  queryParamSubscription: Subscription;
  storeStatusSubscription: Subscription;
  descriptionTitle: string;
  membershipLinkText: string;
  membershipLink: string;
  membershipEditText = 'To edit, visit ';
  membershipDetailsText = 'membership details';
  externalBaseUrl = environment.snFrontendBaseUrl;
  upfrontAmountSettingText: string;
  showUpfrontAmountText = false;
  showItemModal = false;
  archivingItem = false;
  variationDescription: string;
  priceSchedules: Array<PriceSchedule> = [];
  variationColumns: string[] = ['variation', 'price', 'sold'];
  saleColumns: string[] = ['created_at', 'type', 'sale', 'member', 'price', 'quantity', 'item_total'];
  salesPaginationOptions: SeFePaginationOptions = {
    label: 'Sales',
    name: 'salesPagination',
    currentPage: 1,
    limit: 25,
    total: 0,
  };

  constructor(
    @Inject(CURRENT_ORG_ID) public currentOrgId: string,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone,
    private membershipDefinitionService: MembershipDefinitionService,
    private itemService: ItemService,
    private itemVariationService: ItemVariationService,
    private saleItemService: SaleItemService,
    private currencyPipe: CurrencyPipe,
    private pagingService: PagingService,
    private storeStatusService: StoreStatusService,
    private confirmService: SeFeConfirmService,
    private toastService: SeFeToastService,
    public externalLinkService: AppExternalLinkService,
    private seAnalyticsService: SeAnalyticsService,
    private surveyResultsService: SurveyResultsService,
    private loadScriptsService: LoadScriptsService,
    private priceScheduleService: PriceScheduleService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.loadScriptsService
      .loadScripts('saleItem')
      .catch((error) => datadogRum.addError('Unable to load sale item modal', { error }));
    this.setSalesPaginationLabel();
    this.loadItem();
    this.subscribeToRouteParams();
    this.subscribeToStoreStatus();
    if (!this.storeStatusService.isSet) {
      this.storeStatusService.setStoreStatus(this.currentOrgId);
    }
    this.seAnalyticsService.trackEvent('pageView', {
      depth1: 'HQ',
      depth2: 'SaleItems',
      depth3: 'Detail',
    });
    datadogRum.addAction('Viewing Sale Item Detail', {
      itemId: this.itemId,
      orgId: this.currentOrgId,
    });
  }

  ngOnDestroy() {
    this.pagingService.unsubscribe();
    this.queryParamSubscription.unsubscribe();
    this.storeStatusSubscription.unsubscribe();
  }

  loadItem(): void {
    this.loadingItem = true;
    forkJoin([
      this.itemService.getItem(this.itemId).pipe(
        catchError((error: HttpErrorResponse) =>
          throwError((): ItemDetailLoadError => ({ source: 'item', error }))
        )
      ),
      this.itemVariationService.getItemVariations(this.combinedVariationApiParams()).pipe(
        catchError((error: HttpErrorResponse) =>
          throwError((): ItemDetailLoadError => ({ source: 'variations', error }))
        )
      ),
    ]).subscribe({
      next: ([response, statsResponse]) => {
        this.loadVariationStatsSuccess(statsResponse);
        this.loadItemSuccess(response);
      },
      error: ({ source, error }: ItemDetailLoadError) => {
        if (source === 'item' && error.status === 404) {
          this.redirectToArchivedItems();
          return;
        }

        this.loadItemError(error);
      },
    });
  }

  loadItemSuccess(response: ApiResponse<Item>): void {
    this.item = new Item(response.result);
    this.defaultVariation = this.item.getDefaultVariation();
    this.variationHasSku = this.item.variations.some((variation: ItemVariation) => !!variation.sku);
    this.setVariationColumns();
    this.setMembershipInfo();
    this.showUpfrontAmountText = this.defaultVariation.price_cents > 0;
    if (this.showUpfrontAmountText) {
      this.upfrontAmountSettingText = this.itemVariationService.upfrontAmountSettingText(this.defaultVariation);
    }

    this.loadPriceSchedules();

    this.variationDescription = this.item.variations.length > 1 ? 'includes_all_variations' : 'null';
  }

  loadVariationStatsSuccess(response: ApiResponse<ItemVariation[]>): void {
    response.result.forEach((variationStat: any) => {
      this.variationStats[variationStat.id] = variationStat;
    });
  }

  loadPriceSchedules() {
    const param = { item_variation_uuid: this.defaultVariation.uuid };
    this.priceScheduleService.getPriceSchedules(param).subscribe(
      (response) => {
        this.priceSchedules = response.result;
        this.loadingItem = false;
      },
      () => {
        this.loadingItem = false;
      }
    );
  }

  setMembershipInfo(): void {
    if (this.item && this.item.membership) {
      this.membershipDefinitionService.getMembershipDefinition(this.item.uuid, this.currentOrgId).subscribe(
        (response) => this.showDescription(response.result, this.currentOrgId),
        () => this.showDescriptionTitleOnly()
      );
    }
  }

  showDescription(result: MembershipDefinition[], orgId: string): void {
    if (result.length) {
      this.membershipLink = this.getMembershipUrl(result[0].id, orgId);
      this.descriptionTitle = this.membershipEditText;
      this.membershipLinkText = this.membershipDetailsText;
    } else {
      this.showDescriptionTitleOnly();
    }
  }

  showDescriptionTitleOnly(): void {
    this.descriptionTitle = this.membershipEditText + this.membershipDetailsText;
  }

  hideEditAction(): boolean {
    return this.loadingSaleItems ? true : this.item.membership;
  }

  getMembershipUrl(id: string, orgId: string): string {
    return this.membershipDefinitionService.getMembershipUrl(id, orgId);
  }

  loadItemError(error: Error): void {
    datadogRum.addError('Error loading item detail', {
      error: error,
      message: error.message,
      item: this.item,
      saleItems: this.saleItems,
      storeStatus: this.storeStatusService.$storeStatus,
      currentOrgId: this.currentOrgId,
    });
    throw error;
  }

  private redirectToArchivedItems(): void {
    this.loadingItem = false;
    this.router.navigate(['../..'], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: 'archived' },
    });
  }

  subscribeToRouteParams(): void {
    this.queryParamSubscription = this.activatedRoute.queryParamMap.subscribe(() => {
      this.loadSaleItems();
    });
  }

  combinedVariationApiParams(): Params {
    const params = isNaN(this.itemId) ? { item_uuid: this.itemId } : { item_id: this.itemId };
    return this.pagingService.toApiParams(params);
  }

  combinedApiParams(): Params {
    const params = {
      item_id: this.itemId,
      sold: true,
    };
    return this.pagingService.toApiParams(params);
  }

  loadSaleItems(): void {
    this.showOverlay = true;
    this.saleItemService.getSaleItems(this.combinedApiParams()).subscribe((response: ApiResponse<SaleItem[]>) => {
      this.saleItems = response.result.map((saleItem) => new SaleItem(saleItem));
      this.collectSalesPagination(response.metadata.pagination);
      this.generateSalesSummary(response.metadata.summary);
      this.hasSaleItems = !!this.saleItems.length;
      this.loadingSaleItems = false;
      this.showOverlay = false;
      this.saleItems.forEach((saleItem) => {
        if (saleItem.sale?.persona_id) {
          this.getProfileUrl(saleItem);
        }
      });
    });
  }

  navigate(params: Params, strategy: QueryParamsHandling = 'merge'): void {
    this.ngZone.run(() => {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: params,
        queryParamsHandling: strategy,
      });
    });
  }

  public handleSalesPagination(paginationData: SeFePaginationChange): void {
    const { options, queryParams } = this.pagingService.applyPaginationChange(
      paginationData,
      this.salesPaginationOptions
    );
    this.salesPaginationOptions = options;
    this.navigate(queryParams);
  }

  public async getProfileUrl(saleItem: SaleItem): Promise<void> {
    if (saleItem.profileUrl !== undefined) {
      return;
    }

    const { metadata } = saleItem;
    if (!metadata) {
      if (saleItem.sale?.persona_id) {
        saleItem.profileUrl = this.externalLinkService.getMemberUrl(saleItem.sale.persona_id.toString());
      } else {
        saleItem.profileUrl = null;
      }
      return;
    }
    if (metadata.memberships?.persona_ids?.[0]) {
      saleItem.profileUrl = this.externalLinkService.getMemberUrl(metadata.memberships.persona_ids[0]);
    } else if (metadata.survey_result?.id) {
      try {
        const data = await this.surveyResultsService.get(metadata.survey_result.id).toPromise();
        if (data?.result?.persona_id) {
          saleItem.profileUrl = this.externalLinkService.getMemberUrl(data.result.persona_id);
        } else {
          saleItem.profileUrl = null;
        }
      } catch (error) {
        console.error('Error fetching survey result:', error);
        saleItem.profileUrl = null;
      }
    } else if (saleItem.sale?.persona_id) {
      saleItem.profileUrl = this.externalLinkService.getMemberUrl(saleItem.sale.persona_id.toString());
    } else {
      saleItem.profileUrl = null;
    }
  }

  generateSalesSummary(summaryObj: SalesSummary): void {
    const currency = this.getCurrency(this.saleItems);
    const totalAmountInDollars = summaryObj.total_amount / 100;
    const formattedTotalAmount = this.currencyPipe.transform(totalAmountInDollars, currency, 'symbol', '1.2-2');
    this.salesSummary = `${summaryObj.total_quantity} sold • ${formattedTotalAmount} total`;
  }

  public onSuccess(item: Item): void {
    this.toastService.addToast({
      type: SeFeToastTypes.Success,
      message: `"${item.name}" sale item updated`,
    });
    this.loadItem();
  }

  public toggleItemModal(): void {
    this.showItemModal = !this.showItemModal;
  }

  public confirmArchive(event: MouseEvent): void {
    if (!this.item || this.item.disabled || this.archivingItem) {
      return;
    }

    this.confirmService.confirm({
      title: this.translateService.instant('LIST_VIEW.ARCHIVE_MODAL.title', { name: this.item.name }),
      message: this.translateService.instant('LIST_VIEW.ARCHIVE_MODAL.message'),
      actions: [
        {
          label: this.translateService.instant('LIST_VIEW.ARCHIVE_MODAL.cancel'),
          action: () => null,
        },
        {
          label: this.translateService.instant('LIST_VIEW.ARCHIVE_MODAL.confirm_action'),
          primary: true,
          action: () => this.archiveItem(),
        },
      ],
      triggerEl: event.currentTarget as HTMLElement,
    });
  }

  public archiveItem(): void {
    if (!this.item || this.item.disabled || this.archivingItem) {
      return;
    }

    this.archivingItem = true;
    this.itemService.deleteItem(this.item.id).subscribe({
      next: () => {
        this.toastService.addToast({
          type: SeFeToastTypes.Success,
          message: this.translateService.instant('LIST_VIEW.ARCHIVE_MODAL.success', { name: this.item.name }),
        });
        this.router.navigate(['../..'], { relativeTo: this.activatedRoute });
      },
      error: (error) => {
        this.archivingItem = false;
        this.toastService.addToast({
          type: SeFeToastTypes.Error,
          message: this.archiveItemErrorMessage(error),
        });
      },
    });
  }

  private subscribeToStoreStatus(): void {
    this.storeStatusSubscription = this.storeStatusService.$storeStatus.subscribe((storeStatus) => {
      this.showTaxCode = storeStatus.taxable;
    });
  }

  private getCurrency(saleItems: SaleItem[]): string {
    return saleItems.length ? saleItems[0].currency : 'USD';
  }

  private archiveItemErrorMessage(error: any): string {
    return (
      error?.error?.error?.messages?.[0] ||
      error?.error?.messages?.[0] ||
      this.translateService.instant('LIST_VIEW.ARCHIVE_MODAL.ERROR.default', { name: this.item.name })
    );
  }

  private collectSalesPagination(pagination: PagedParameters | undefined): void {
    this.salesPaginationOptions = this.pagingService.syncPaginationFromResponse(
      pagination,
      this.salesPaginationOptions
    );
  }

  private setSalesPaginationLabel(): void {
    this.salesPaginationOptions = {
      ...this.salesPaginationOptions,
      label: this.translateService.instant('DETAIL_VIEW.sales') || 'Sales',
    };
  }

  private setVariationColumns(): void {
    const columns = ['variation', 'price'];

    if (this.variationHasSku) {
      columns.push('sku');
    }

    columns.push('sold');
    this.variationColumns = columns;
  }

  public getFinancialReportURL(): string {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const params = {
      export: false,
      sortOrder: 'desc',
      page: 1,
      perPage: 25,
      orderBy: 'sale_date',
      startDate: `${currentYear}-01-01`,
      endDate: `${currentYear}-12-31`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
      dynamicFilters: {
        item_name: [this.item?.name || this.defaultVariation?.name || ''],
      },
    };

    const encodedParams = encodeURIComponent(JSON.stringify(params));
    return `${environment.snFrontendBaseUrl}/org/${this.currentOrgId}/financial_reports/sale-items/${this.itemId}?params=${encodedParams}&quickFilter=fiscal-year`;
  }

  public zeroRateType(): number | string {
    switch (this.defaultVariation.zero_rate_type) {
      case null:
        return 'Registration and Invoicing';
      case 'background_screen':
        return 'Safety: NCSI Background Screening';
      case 'abuse_prevention_system':
        return 'Safety: Abuse Prevention System (APS) Training';
      default:
        return this.defaultVariation.zero_rate_type || '--';
    }
  }
}
